import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  User,
  FileText,
  Home,
  BookOpen,
  Code,
  Upload,
  History,
  Settings,
} from "lucide-react";
import NotificationsPanel from "./NotificationsPanel";
import "./COCBLayout.css";

function COCBLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Get API Docs URL for COCB pipeline
  const getCOCBApiDocsUrl = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:5001/api/extraction/cocb/v1/docs';
    }
    return `${window.location.protocol}//${window.location.hostname}/api/extraction/cocb/v1/docs`;
  };

  const menuItems = [
    { path: "/v1/cocb", icon: Home, label: "Dashboard", exact: true },
    { path: "/v1/cocb/dashboard", icon: Home, label: "Dashboard" },
    { path: "/v1/cocb/extract", icon: Upload, label: "Extract Document", color: "#10b981" },
    { path: "/v1/cocb/history", icon: History, label: "Work Orders", color: "#6366f1" },
    { path: "/v1/cocb/api-logs", icon: Code, label: "API Logs" },
    { path: getCOCBApiDocsUrl(), icon: BookOpen, label: "API Docs", external: true },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    if (item.path === "/v1/cocb/dashboard" && (location.pathname === "/v1/cocb" || location.pathname === "/v1/cocb/")) {
      return true;
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    onLogout();
    navigate("/login");
  };

  const getPageTitle = () => {
    if (location.pathname === "/v1/cocb" || location.pathname === "/v1/cocb/" || location.pathname === "/v1/cocb/dashboard") {
      return "COCB Dashboard";
    }
    if (location.pathname === "/v1/cocb/extract") {
      return "Extract Document";
    }
    if (location.pathname === "/v1/cocb/history") {
      return "Work Orders";
    }
    if (location.pathname.startsWith("/v1/cocb/work-order/")) {
      return "Work Order Details";
    }
    const item = menuItems.find(m => isActive(m) && !m.exact);
    if (item) {
      return item.label;
    }
    return "COCB Pipeline";
  };

  return (
    <div className="cocb-layout">
      {/* Sidebar */}
      <aside className="cocb-sidebar">
        <div className="cocb-sidebar-header">
        {(user?.pipeline === "all" || user?.role === "superadmin" || user?.role === "admin") && (
          <button onClick={() => navigate("/dashboard")} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",margin:"0 12px 8px",background:"#f1f5f9",border:"1px solid #e2e8f0",borderRadius:6,cursor:"pointer",fontSize:12,color:"#475569",fontWeight:500}} title="Back to Main Dashboard">
            <span style={{fontSize:14}}>←</span> Main Dashboard
          </button>
        )}
          <div className="cocb-logo">
            <FileText size={24} className="cocb-logo-icon" />
            <span className="cocb-logo-text">COCB Pipeline</span>
          </div>
          <div className="cocb-logo-subtitle">Change of Distributor</div>
        </div>

        <nav className="cocb-sidebar-nav">
          {menuItems.filter(item => !item.exact).map((item) => (
            <button
              key={item.path}
              className={`cocb-nav-item ${isActive(item) ? "active" : ""} ${item.external ? "cocb-nav-item-external" : ""}`}
              onClick={() => {
                if (item.external) {
                  window.open(item.path, "_blank");
                } else {
                  navigate(item.path);
                }
              }}
              title={item.label}
              style={item.color ? { "--item-color": item.color } : {}}
            >
              <item.icon size={20} style={item.color ? { color: item.color } : {}} />
              <div className="cocb-nav-item-content">
                <span className="cocb-nav-label">{item.label}</span>
              </div>
            </button>
          ))}
        </nav>

        <div className="cocb-sidebar-footer">
          <button
            className="cocb-nav-item logout"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
            <div className="cocb-nav-item-content">
              <span className="cocb-nav-label">Logout</span>
            </div>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="cocb-layout-content">
        {/* Top Header */}
        <header className="cocb-top-header">
          <div className="cocb-header-title">{getPageTitle()}</div>

          <div className="cocb-header-actions">
            <NotificationsPanel />
            
            <div className="cocb-user-menu">
              <button
                className="cocb-user-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="cocb-user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="cocb-user-name">{user?.name || user?.email}</span>
              </button>

              {showUserMenu && (
                <div className="cocb-user-dropdown">
                  <div className="cocb-dropdown-header">
                    <User size={18} />
                    <div>
                      <div className="cocb-dropdown-name">{user?.name || 'COCB User'}</div>
                      <div className="cocb-dropdown-email">{user?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <button
                    className="cocb-dropdown-item logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="cocb-page-content">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

export default COCBLayout;
